import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, serverTimestamp, writeBatch } from 'firebase/firestore';
import { auth, db } from '../firebase';
// NOTE: Signup is a public route; do not depend on AuthContext providers here.

import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { UserPlus, Calendar } from 'lucide-react';
import cleanbidLogo from '../assets/cleanbid-logo.png';

const SignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    if (!firstName.trim()) {
      setError('First name is required');
      return false;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return false;
    }
    if (!email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!companyName.trim()) {
      setError('Company name is required');
      return false;
    }
    if (!password) {
      setError('Password is required');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Firebase config is defined in ../firebase (no debug logging in production)

      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email.trim(), password);
      const uid = userCredential.user.uid;
      const companyId = uid; // deterministic for now

      // In one logical batch, create user identity, company, and membership.
      const batch = writeBatch(db);

      const safeEmail = email.trim();
      const safeCompanyName = companyName.trim();

      // a) User identity
      const userRef = doc(db, 'users', uid);
      batch.set(
        userRef,
        {
          email: safeEmail,
          displayName: `${firstName.trim()} ${lastName.trim()}`.trim(),
          companyId,
          status: 'active',
          onboardingComplete: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // b) Company
      const companyRef = doc(db, 'companies', companyId);
      batch.set(
        companyRef,
        {
          name: safeCompanyName,
          ownerUid: uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // c) Membership (owner role)
      const membershipRef = doc(db, 'companies', companyId, 'users', uid);
      batch.set(
        membershipRef,
        {
          role: 'owner',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();

      // New owner → redirect to onboarding wizard
      navigate('/onboarding');
    } catch (err) {
      console.error('[SignUp] Signup error:', err);

      // --- ORPHANED AUTH USER RECOVERY ---
      // If Firebase Auth creation succeeded but the Firestore batch failed on a
      // previous attempt, the user is stuck: they cannot sign up again (email
      // taken) but have no Firestore profile. Detect this and rebuild only the
      // missing documents so the user can proceed.
      if (err.code === 'auth/email-already-in-use') {
        try {
          // Sign in with the credentials the user just entered to recover the UID.
          const { signInWithEmailAndPassword } = await import('firebase/auth');
          const { doc: _doc, getDoc, setDoc: _setDoc, serverTimestamp: _ts, writeBatch: _wb } = await import('firebase/firestore');

          const recoveryCred = await signInWithEmailAndPassword(auth, email.trim(), password);
          const recoveryUid = recoveryCred.user.uid;
          const recoveryCompanyId = recoveryUid;

          // Check whether the user profile document exists.
          const userSnap = await getDoc(_doc(db, 'users', recoveryUid));

          if (!userSnap.exists()) {
            // Profile missing — the batch failed on first signup. Rebuild.
            console.log('[SignUp] Orphaned Auth user detected — rebuilding Firestore documents');

            const recoveryBatch = _wb(db);
            const safeEmail = email.trim();
            const safeCompanyName = companyName.trim();

            // Rebuild users/{uid} — only if missing (already checked above)
            recoveryBatch.set(
              _doc(db, 'users', recoveryUid),
              {
                email: safeEmail,
                displayName: `${firstName.trim()} ${lastName.trim()}`.trim(),
                companyId: recoveryCompanyId,
                status: 'active',
                onboardingComplete: false,
                createdAt: _ts(),
                updatedAt: _ts(),
              },
              { merge: true }
            );

            // Rebuild companies/{uid} only if missing
            const companySnap = await getDoc(_doc(db, 'companies', recoveryCompanyId));
            if (!companySnap.exists()) {
              recoveryBatch.set(
                _doc(db, 'companies', recoveryCompanyId),
                {
                  name: safeCompanyName || 'My Company',
                  ownerUid: recoveryUid,
                  createdAt: _ts(),
                  updatedAt: _ts(),
                },
                { merge: true }
              );
            }

            // Rebuild membership only if missing
            const memberSnap = await getDoc(_doc(db, 'companies', recoveryCompanyId, 'users', recoveryUid));
            if (!memberSnap.exists()) {
              recoveryBatch.set(
                _doc(db, 'companies', recoveryCompanyId, 'users', recoveryUid),
                {
                  role: 'owner',
                  createdAt: _ts(),
                  updatedAt: _ts(),
                },
                { merge: true }
              );
            }

            await recoveryBatch.commit();
            console.log('[SignUp] Recovery batch committed — navigating to onboarding');
            navigate('/onboarding');
            return;
          } else {
            // Profile exists — this is a genuine duplicate signup attempt.
            setError('This email is already registered. Please sign in instead.');
          }
        } catch (recoveryErr) {
          console.error('[SignUp] Recovery attempt failed:', recoveryErr);
          // Recovery failed (likely wrong password on existing account).
          setError('This email is already registered. Please sign in instead.');
        }
        return;
      }

      if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use a stronger password.');
      } else if (
        err.code === 'auth/operation-not-allowed' ||
        err.code === 'auth/network-request-failed'
      ) {
        setError('Invite-only beta. Signups closed.');
      } else {
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0F] p-4">

      <div
        className="w-full max-w-md rounded-2xl border border-[#1E1E2E] p-8"
        style={{
          backgroundColor: '#111118',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.04)',
        }}
      >
        {/* CleanBid.ai brand lockup */}
        <div className="flex flex-col items-center" style={{ marginBottom: '24px' }}>
          <img
            src={cleanbidLogo}
            alt="CleanBid.ai"
            style={{ height: '56px', width: 'auto', marginBottom: '10px' }}
          />
          <span style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#F4F4F8' }}>
            CleanBid<span style={{ color: '#71DED6' }}>.ai</span>
          </span>
        </div>

        {/* Headlines */}
        <div className="text-center" style={{ marginBottom: '16px' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#F4F4F8', marginBottom: '4px' }}>
            Create Account
          </h1>
          <p style={{ fontSize: '0.875rem', fontWeight: 400, color: '#8B8BA7' }}>
            Sign up for CleanBid.ai
          </p>
        </div>

        {/* Value props */}
        <div className="text-center" style={{ marginBottom: '24px' }}>
          {[
            'SMS quotes that close faster',
            'Follow-ups that run automatically',
            'Reviews that build your reputation',
          ].map((prop) => (
            <p key={prop} style={{ fontSize: '0.875rem', color: '#8B8BA7', lineHeight: '1.8' }}>
              <span style={{ color: '#71DED6', marginRight: '6px' }}>&#10003;</span>
              {prop}
            </p>
          ))}
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Calendly CTA */}
        <div style={{ marginBottom: '24px' }}>
          <a
            href="https://calendly.com/placeholder"
            target="_blank"
            rel="noreferrer"
            className="block"
          >
            <button
              type="button"
              className="w-full h-11 rounded-lg flex items-center justify-center gap-2 font-semibold text-sm"
              style={{
                backgroundColor: 'rgba(113, 222, 214, 0.1)',
                border: '1px solid #71DED6',
                color: '#71DED6',
                transition: 'background-color 200ms ease, border-color 200ms ease',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(113, 222, 214, 0.18)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(113, 222, 214, 0.1)'}
            >
              <Calendar className="h-4 w-4" />
              Book a Call
            </button>
          </a>
          <p style={{ marginTop: '8px', fontSize: '0.75rem', color: '#3A3A52', textAlign: 'center' }}>
            Prefer to talk to someone first? Book a quick call and we&apos;ll walk you through CleanBid.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="firstName" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>First Name</Label>
              <Input
                id="firstName"
                type="text"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>Last Name</Label>
              <Input
                id="lastName"
                type="text"
                placeholder="Doe"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={isLoading}
                className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="companyName" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>Company Name</Label>
            <Input
              id="companyName"
              type="text"
              placeholder="My Company"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              disabled={isLoading}
              className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword" style={{ color: '#8B8BA7', fontSize: '0.875rem' }}>Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              className="h-10 bg-[#0A0A0F] border-[#1E1E2E] text-[#F4F4F8] placeholder:text-[#3A3A52] focus-visible:border-[#71DED6] focus-visible:ring-1 focus-visible:ring-[#71DED6]/20"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-11 rounded-lg font-semibold hover:opacity-90 transition-opacity border-0"
            style={{ backgroundColor: '#71DED6', color: '#10151D', fontWeight: 600 }}
            size="lg"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 border-2 border-[#10151D] border-t-transparent rounded-full animate-spin" />
                Creating account...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Create Account
              </span>
            )}
          </Button>
        </form>

        {/* Sign in link */}
        <div className="mt-6 text-center">
          <p style={{ fontSize: '0.875rem', color: '#8B8BA7' }}>
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold hover:underline"
              style={{ color: '#71DED6' }}
            >
              Sign In
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p style={{ fontSize: '0.75rem', fontStyle: 'italic', color: '#3A3A52' }}>
            Powered by CleanBid.ai
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
